import { TFunction } from "i18next";
import moment from "moment";
import { date } from "yup";
import type { RequiredDateSchema } from "yup/lib/date";

import { createDateLabel, formatDate, toEndOfCalendarDay, toStartOfCalendarDay } from "utils/format";

export function resolveMinimumStartDate(today: Date, currentStartDate?: string): Date {
    const startOfToday = toStartOfCalendarDay(today);
    if (currentStartDate === undefined) {
        return startOfToday;
    }
    const startOfCurrentStartDate = toStartOfCalendarDay(currentStartDate);
    return startOfCurrentStartDate < startOfToday ? startOfCurrentStartDate : startOfToday;
}

export function resolveStartDateToSubmit(selectedStartDate: string, storedStartDate?: string): string {
    if (storedStartDate !== undefined && isSameCalendarDay(storedStartDate, selectedStartDate)) {
        return storedStartDate;
    }
    return formatDate(moment(toStartOfCalendarDay(selectedStartDate)));
}

export function resolveExpirationDateToSubmit(selectedExpirationDate: string): string {
    return formatDate(moment(toEndOfCalendarDay(selectedExpirationDate)));
}

export function createStartDateSchema(
    t: TFunction,
    minimumStartDate: Date,
    licenseExpirationDate: string
): RequiredDateSchema<Date | undefined> {
    const schema = date()
        .required(t("Licenses.bms.assignDialog.startDateRequiredError"))
        .min(minimumStartDate, (params) =>
            t("Licenses.bms.assignDialog.startDateMinError", { min: createDateLabel(params.min) })
        );
    const latestDate = resolveLatestDate(licenseExpirationDate);
    if (latestDate === undefined) {
        return schema;
    }
    return schema.max(latestDate, (params) =>
        t("Licenses.bms.assignDialog.startDateMaxError", { max: createDateLabel(params.max) })
    );
}

export function createExpirationDateSchema(
    t: TFunction,
    licenseExpirationDate: string
): RequiredDateSchema<Date | undefined> {
    const schema = date().required(t("Licenses.bms.assignDialog.expirationDateRequiredError"));
    const latestDate = resolveLatestDate(licenseExpirationDate);
    const boundedSchema =
        latestDate === undefined
            ? schema
            : schema.max(latestDate, (params) =>
                  t("Licenses.bms.assignDialog.expirationDateMaxError", { max: createDateLabel(params.max) })
              );
    return boundedSchema.when(
        "startDate",
        (startDate: string | undefined, expirationSchema: RequiredDateSchema<Date | undefined>) => {
            const earliestDate = startDate === undefined ? undefined : toValidDate(toStartOfCalendarDay(startDate));
            // A missing or unusable start date is reported by the start date validation, so no bound is applied here.
            if (earliestDate === undefined) {
                return expirationSchema;
            }
            return expirationSchema.min(earliestDate, (params) =>
                t("Licenses.bms.assignDialog.expirationDateMinError", { min: createDateLabel(params.min) })
            );
        }
    );
}

function resolveLatestDate(licenseExpirationDate: string): Date | undefined {
    return toValidDate(toEndOfCalendarDay(licenseExpirationDate));
}

function toValidDate(value: Date): Date | undefined {
    return isNaN(value.getTime()) ? undefined : value;
}

function isSameCalendarDay(first: string, second: string): boolean {
    return toStartOfCalendarDay(first).getTime() === toStartOfCalendarDay(second).getTime();
}
